import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSession,
  fetchSession,
  submitAnswer,
  fetchResults,
  fetchActiveExam,
  saveExamState,
} from '../api/sessions';
import { useCertStore } from '../stores/certStore';
import type {
  CreateSessionPayload,
  SubmitAnswerPayload,
  ExamStateUpdatePayload,
  ActiveExam,
  Session,
  SessionResults,
} from '../types';

/** Create a new study / exam session. */
export function useCreateSession() {
  const queryClient = useQueryClient();
  const certCode = useCertStore((s) => s.certCode);

  return useMutation({
    mutationFn: (payload: CreateSessionPayload) => createSession(payload, certCode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/** Fetch a session by ID. */
export function useSession(id: number | null) {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery<Session>({
    queryKey: ['session', certCode, id],
    queryFn: () => fetchSession(id!),
    enabled: id !== null,
  });
}

/** Submit an answer within a session. */
export function useSubmitAnswer(sessionId: number | null) {
  const queryClient = useQueryClient();
  const certCode = useCertStore((s) => s.certCode);

  return useMutation({
    mutationFn: (payload: SubmitAnswerPayload) => {
      if (sessionId === null) throw new Error('No active session');
      return submitAnswer(sessionId, payload);
    },
    onSuccess: () => {
      if (sessionId !== null) {
        void queryClient.invalidateQueries({ queryKey: ['session', certCode, sessionId] });
      }
    },
  });
}

/** Fetch results for a completed session. */
export function useSessionResults(sessionId: number | null) {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery<SessionResults>({
    queryKey: ['session-results', certCode, sessionId],
    queryFn: () => fetchResults(sessionId!),
    enabled: sessionId !== null,
  });
}

/** Check for an active (in-progress) exam session. */
export function useActiveExam() {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery<ActiveExam | null>({
    queryKey: ['active-exam', certCode],
    queryFn: () => fetchActiveExam(certCode),
    staleTime: 0,
    retry: false,
  });
}

/** Save exam state periodically. */
export function useSaveExamState(sessionId: number | null) {
  return useMutation({
    mutationFn: (payload: ExamStateUpdatePayload) => {
      if (sessionId === null) throw new Error('No active session');
      return saveExamState(sessionId, payload);
    },
  });
}
